import React from 'react';
import { createPageUrl } from '@/utils';

// Simple error boundary to avoid white screens in production.
// It shows a friendly message and keeps the app usable.
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, componentStack: '' };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error('[TrésoPilot] Unhandled UI error:', error, info);

    // Store a debug payload (user can paste it to support).
    try {
      const payload = {
        message: error?.message || String(error),
        stack: error?.stack || null,
        componentStack: info?.componentStack || null,
        href: typeof window !== 'undefined' ? window.location.href : null,
        ts: new Date().toISOString(),
      };
      if (typeof window !== 'undefined') {
        window.__TRESOPILOT_LAST_UI_ERROR__ = payload;
        localStorage.setItem('tresopilot:last_ui_error', JSON.stringify(payload));
      }
    } catch (_) {
      // ignore
    }

    this.setState({ componentStack: info?.componentStack || '' });
  }

  render() {
    if (!this.state.hasError) return this.props.children;

    const message = this.state.error?.message || 'Une erreur inattendue est survenue.';

    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-slate-50">
        <div className="max-w-lg w-full bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
          <h1 className="text-xl font-semibold text-slate-900">Oups…</h1>
          <p className="text-slate-600 mt-2">
            L’application a rencontré un problème d’affichage.
          </p>
          <div className="mt-4 p-3 rounded-xl bg-slate-50 border border-slate-200 text-sm text-slate-700 break-words">
            {message}
          </div>

          {/* Helpful debug info for production (kept collapsible). */}
          <details className="mt-4 text-xs text-slate-600">
            <summary className="cursor-pointer select-none">Détails techniques (pour support)</summary>
            <div className="mt-2 p-3 rounded-xl bg-slate-50 border border-slate-200 whitespace-pre-wrap break-words">
              {this.state.componentStack || 'Aucune stack React disponible.'}
              {'\n\n'}
              {this.state.error?.stack || ''}
            </div>
            <p className="mt-2 text-slate-500">
              Astuce : si vous voyez une erreur React "Minified" (ex: #310), gardez cette section et envoyez-la.
            </p>
          </details>
          <div className="mt-5 flex flex-col sm:flex-row gap-3">
            {/* NOTE: this fallback renders *outside* the Router tree, so we must NOT use <Link /> here. */}
            <a
              href={createPageUrl('Landing')}
              className="inline-flex items-center justify-center rounded-xl px-4 py-2 bg-emerald-600 text-white hover:bg-emerald-700 transition"
            >
              Retour à l’accueil
            </a>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="inline-flex items-center justify-center rounded-xl px-4 py-2 border border-slate-300 text-slate-700 hover:bg-slate-100 transition"
            >
              Recharger
            </button>
          </div>
        </div>
      </div>
    );
  }
}
